import React, { useEffect, useMemo, useState } from "react";
import { useI18n } from "@/i18n";
import { useRouter } from "@/router/Router";
import { useCatalog } from "@/state/CatalogContext";
import { useConfig } from "@/lib/useConfig";
import { useCategories, inCategory } from "@/lib/categories";
import { fetchLatestReviews, fetchPosts, mediaUrl } from "@/lib/api";
import { DI, DProductCard, DSkeletonCard, GROUP_KINDS, PATH, WI, inSlug, useDesktopNav } from "@/components/desktop/desktopUi";
import type { PostSummary, Review } from "@/types";
import { LiveHeroPhoto } from "@/components/desktop/LiveHero";

/** Desktop home page — follows the approved reference layout; every photo is a slot in the admin. */
export function DesktopHome() {
  const { t, locale } = useI18n();
  const { navigate } = useRouter();
  const { products, loading } = useCatalog();
  const { site } = useConfig();
  const categories = useCategories();
  const nav = useDesktopNav(categories);
  const [tab, setTab] = useState<string>("all");
  const [posts, setPosts] = useState<PostSummary[]>([]);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [revPage, setRevPage] = useState(0);

  useEffect(() => {
    fetchPosts({ limit: 3 }).then(setPosts).catch(() => setPosts([]));
    fetchLatestReviews(12).then((r) => setReviews(r.items.filter((x) => x.rating >= 4 && x.text.trim().length > 20))).catch(() => setReviews([]));
  }, [locale]);

  // Tiles by group, like the reference: coffee, machines (else grinders), accessories.
  // Drip and other coffee categories belong to the coffee group and get no tile of their own.
  // Photo: the admin slot of the category (or of any category in the group), else a product photo.
  const slotImage = (slugs: string[]) => slugs.map((s) => site?.categoryImages?.[s]).find(Boolean);
  const coffeeCat = nav.coffee[0];
  const machines = nav.equipment?.categories.find((c) => c.kind === "machines");
  const tiles = [
    coffeeCat && { key: "coffee", title: coffeeCat.name, hint: t("desktop.tileHint.coffee"), slug: coffeeCat.slug, img: slotImage([coffeeCat.slug]) || products.find((p) => inCategory(p, coffeeCat.slug))?.images[0] },
    nav.equipment && { key: "equipment", title: machines?.name ?? t("desktop.equipment"), hint: t("desktop.tileHint.machines"), slug: nav.equipment.slug, img: slotImage(nav.equipment.categories.map((c) => c.slug)) || products.find((p) => GROUP_KINDS[nav.equipment!.slug].includes(p.category))?.images[0] },
    nav.accessories && { key: "accessories", title: t("desktop.accessories"), hint: t("desktop.tileHint.accessories"), slug: nav.accessories.slug, img: slotImage(nav.accessories.categories.map((c) => c.slug)) || products.find((p) => GROUP_KINDS[nav.accessories!.slug].includes(p.category))?.images[0] },
  ].filter((x): x is { key: string; title: string; hint: string; slug: string; img: string | undefined } => !!x);
  const firstOfKind = (kind: string) => products.find((p) => p.category === kind)?.images[0];
  const heroImg = site?.heroImage || products.find((p) => p.category === "coffee")?.images[0] || "/brand/hero-pack.webp";

  const tabs = [
    { key: "all", label: t("desktop.allProducts") },
    ...nav.coffee.map((c) => ({ key: c.slug, label: c.name })),
    ...(nav.equipment ? [{ key: nav.equipment.slug, label: t("desktop.equipment") }] : []),
    ...(nav.accessories ? [{ key: nav.accessories.slug, label: t("desktop.accessories") }] : []),
  ];
  const best = useMemo(() => (tab === "all" ? products : products.filter((p) => inSlug(p, tab, inCategory))).slice(0, 6), [products, tab]);
  const shownReviews = reviews.slice(revPage * 3, revPage * 3 + 3);

  return (
    <div className="dh">
      {/* Hero */}
      <section className="dh-hero">
        <span className="dh-hero__art" aria-hidden="true" />
        {site?.heroImage && site.heroMotion !== false ? (
          <LiveHeroPhoto src={mediaUrl(heroImg) ?? heroImg} steam={site.heroSteam} />
        ) : (
          <div className="dh-hero__photo" style={{ backgroundImage: `url(${mediaUrl(heroImg)})` }} data-fallback={!site?.heroImage} />
        )}
        <div className="dh-hero__text">
          <div className="dk-eyebrow">{t("desktop.heroEyebrow")}</div>
          <h1 className="dk-serif">{t("desktop.heroTitle1")}<br />{t("desktop.heroTitle2")}</h1>
          <p>{t("desktop.heroText")}</p>
          <div className="dh-hero__cta">
            <button className="dk-btn" onClick={() => navigate({ name: "catalog", category: nav.coffee[0]?.slug })}>{t("desktop.buyCoffee")} {DI.arrow}</button>
            {nav.equipment && <button className="dk-btn dk-btn--ghost" onClick={() => navigate({ name: "catalog", category: nav.equipment!.slug })}>{t("desktop.seeEquipment")}</button>}
          </div>
          <div className="dh-perks">
            <div>{WI(PATH.bean)}<span>{t("desktop.perkFresh")}</span></div>
            <div>{WI(PATH.leaf)}<span>{t("desktop.perkSelected")}</span></div>
            <div>{WI(PATH.truck)}<span>{t("desktop.perkDelivery")}</span></div>
          </div>
        </div>
      </section>

      <div className="dk-wrap">
        {/* Popular categories */}
        <div className="dh-sec dh-sec--center">
          <h2 className="dk-serif">{t("desktop.popularCategories")}</h2>
          <button className="dk-more" onClick={() => navigate({ name: "catalog" })}>{t("desktop.allCatalog")} {DI.arrow}</button>
        </div>
        <div className="dh-tiles" style={{ gridTemplateColumns: `repeat(${Math.max(1, tiles.length)}, 1fr)` }}>
          {tiles.map((c) => (
            <button key={c.key} className="dh-tile" onClick={() => navigate({ name: "catalog", category: c.slug })}>
              {c.img && <img src={mediaUrl(c.img, 800)} alt="" />}
              <span className="dh-tile__text">
                <span className="dk-serif">{c.title}</span>
                <small>{c.hint}</small>
              </span>
              <span className="dh-tile__go">{DI.arrow}</span>
            </button>
          ))}
        </div>

        {/* Bestsellers */}
        <div className="dh-sec">
          <div className="dh-sec__left">
            <h2 className="dk-serif">{t("desktop.bestsellers")}</h2>
            <div className="dh-tabs">
              {tabs.slice(0, 6).map((x) => (
                <button key={x.key} data-active={tab === x.key} onClick={() => setTab(x.key)}>{x.label}</button>
              ))}
            </div>
          </div>
          <button className="dk-more" onClick={() => navigate({ name: "catalog", category: tab === "all" ? undefined : tab })}>{t("desktop.allCatalog")} {DI.arrow}</button>
        </div>
        <div className="dh-best">
          {best.length === 0 && loading ? Array.from({ length: 6 }, (_, i) => <DSkeletonCard key={i} />) : best.map((p) => <DProductCard key={p.id} product={p} />)}
        </div>
      </div>

      {/* Equipment band */}
      {nav.equipment && (
        <section className="dh-eq">
          <span className="dh-eq__art" aria-hidden="true" />
          <div className="dh-eq__photo" style={{ backgroundImage: `url(${mediaUrl(site?.equipmentImage || firstOfKind("machines") || "")})` }} data-fallback={!site?.equipmentImage} />
          <div className="dh-eq__text">
            <div className="dk-eyebrow">{t("desktop.eqEyebrow")}</div>
            <h3 className="dk-serif">{t("desktop.eqTitle")}</h3>
            <p>{t("desktop.eqText")}</p>
            <button className="dk-btn" onClick={() => navigate({ name: "catalog", category: nav.equipment!.slug })}>{t("desktop.eqCta")} {DI.arrow}</button>
          </div>
          <div className="dh-eq__features">
            <div>{WI(PATH.award)}<span><b>{t("desktop.eqF1")}</b><small>{t("desktop.eqF1s")}</small></span></div>
            <div>{WI(PATH.home)}<span><b>{t("desktop.eqF2")}</b><small>{t("desktop.eqF2s")}</small></span></div>
            <div>{WI(PATH.check)}<span><b>{t("desktop.eqF3")}</b><small>{t("desktop.eqF3s")}</small></span></div>
          </div>
        </section>
      )}

      {/* B2B band */}
      <section className="dh-b2b">
        <div className="dh-b2b__photo" style={{ backgroundImage: `url(${mediaUrl(site?.b2bImage || "/brand/editorial.webp")})`, backgroundPosition: `${site?.b2bImagePos ?? "center"} center` }} />
        <div className="dh-b2b__text">
          <div className="dh-b2b__tag">{t("profile.b2b")}</div>
          <h3 className="dk-serif">{t("desktop.b2bTitle")}</h3>
          <p>{t("desktop.b2bText")}</p>
          <button className="dk-btn dk-btn--light" onClick={() => navigate({ name: "lead", source: "site_home" })}>{t("desktop.b2bCta")} {DI.arrow}</button>
        </div>
        <div className="dh-b2b__features">
          <div>{WI(PATH.bean)}<span>{t("desktop.b2bF1")}</span></div>
          <div>{WI(PATH.gear)}<span>{t("desktop.b2bF2")}</span></div>
          <div>{WI(PATH.people)}<span>{t("desktop.b2bF3")}</span></div>
        </div>
      </section>

      <div className="dk-wrap">
        {/* Blog */}
        {posts.length > 0 && (
          <>
            <div className="dh-sec">
              <h2 className="dk-serif">{t("desktop.blogTitle")}</h2>
              <button className="dk-more" onClick={() => navigate({ name: "posts" })}>{t("desktop.allArticles")} {DI.arrow}</button>
            </div>
            <div className="dh-blog">
              {posts.map((p) => (
                <button key={p.id} className="dh-post" onClick={() => navigate({ name: "post", slug: p.slug })}>
                  {p.coverImage ? <img src={mediaUrl(p.coverImage, 400)} alt="" /> : <span className="dh-post__ph" />}
                  <span className="dh-post__body">
                    <span className="dk-eyebrow">{t(`posts.tabs.${p.type}`)}</span>
                    <span className="dk-serif dh-post__title">{p.title}</span>
                    <span className="dh-post__date">{new Intl.DateTimeFormat(locale === "uz" ? "uz-UZ" : locale === "en" ? "en-GB" : "ru-RU", { day: "numeric", month: "long", year: "numeric" }).format(new Date(p.publishedAt))}</span>
                  </span>
                  <span className="dh-post__go">{DI.arrow}</span>
                </button>
              ))}
            </div>
          </>
        )}

        {/* Reviews */}
        {shownReviews.length > 0 && (
          <>
            <div className="dh-sec">
              <h2 className="dk-serif">{t("desktop.reviewsTitle")}</h2>
              {reviews.length > 3 && (
                <div className="dh-arrows">
                  <button onClick={() => setRevPage((p) => Math.max(0, p - 1))} disabled={revPage === 0} aria-label="‹">{DI.chevL}</button>
                  <button onClick={() => setRevPage((p) => ((p + 1) * 3 < reviews.length ? p + 1 : p))} disabled={(revPage + 1) * 3 >= reviews.length} aria-label="›">{DI.chevR}</button>
                </div>
              )}
            </div>
            <div className="dh-reviews">
              {shownReviews.map((r) => (
                <div key={r.id} className="dh-review">
                  <span className="dh-review__ava">{r.authorName.charAt(0).toUpperCase()}</span>
                  <div>
                    <p>«{r.text.length > 170 ? r.text.slice(0, 167).trimEnd() + "…" : r.text}»</p>
                    <div className="dh-review__foot">
                      <span><b>{r.authorName}</b>{r.product && <small>{r.product.name}</small>}</span>
                      <span className="dh-stars">{"★".repeat(r.rating)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
